import type { TraitToColumnMap } from '@baseball-near-you/common';
import type { Grade } from '@baseball-near-you/prisma';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'next/navigation';
import { clients } from '../clients';
import { QueryKey } from './QueryKey';
import { useSpecificTeam } from './useSpecificTeam';

export function getAverageGrades(data: Grade[]) {
  const summedGrades = data.reduce(
    (acc, curr) => {
      return {
        culture: acc.culture + curr.culture,
        facilities: acc.facilities + curr.facilities,
        costValue: acc.costValue + curr.costValue,
        competition: acc.competition + curr.competition,
        communication: acc.communication + curr.communication,
        recruiting: acc.recruiting + curr.recruiting,
        coaching: acc.coaching + curr.coaching,
        playerDevelopment: acc.playerDevelopment + curr.playerDevelopment,
      };
    },
    {
      culture: 0,
      facilities: 0,
      costValue: 0,
      competition: 0,
      communication: 0,
      recruiting: 0,
      coaching: 0,
      playerDevelopment: 0,
    }
  );

  const averageGrades = {
    culture: summedGrades.culture / data.length,
    facilities: summedGrades.facilities / data.length,
    costValue: summedGrades.costValue / data.length,
    competition: summedGrades.competition / data.length,
    communication: summedGrades.communication / data.length,
    recruiting: summedGrades.recruiting / data.length,
    coaching: summedGrades.coaching / data.length,
    playerDevelopment: summedGrades.playerDevelopment / data.length,
  };

  return averageGrades;
}

export function getOverallGrade(averageGrade: Grade) {
  const { teamId, reviewId, id, ...grades } = averageGrade;
  return Number(
    (
      Object.values(grades).reduce((acc, curr) => acc + curr, 0) /
      Object.values(grades).length
    ).toFixed(1)
  );
}

export function useSpecificTeamGrades() {
  const params = useParams();
  const { team } = useSpecificTeam(params.slug as string);

  const {
    data,
    isLoading: isLoadingGrades,
    error: gradesError,
  } = useQuery({
    queryKey: [QueryKey.Team, team?.id, 'grades'],
    queryFn: () =>
      clients.team.getGrades({
        args: {
          teamId: team?.id!,
        },
      }),
    select(data): {
      averageGrades: Record<
        (typeof TraitToColumnMap)[keyof typeof TraitToColumnMap],
        number
      >;
      data: Grade[];
      overallGrade: number | null;
    } {
      const averageGrades = getAverageGrades(data);
      const overallGrade = getOverallGrade({
        ...averageGrades,
        // these are filtered out anyway
        teamId: team?.id!,
        reviewId: data?.[0]?.reviewId ?? null,
        id: data?.[0]?.id ?? null,
      });

      return {
        data: data,
        averageGrades: { ...averageGrades },
        overallGrade: overallGrade ? Number(overallGrade) : null,
      };
    },
  });

  return {
    grades: data?.data,
    averageGrades: data?.averageGrades,
    overallGrade: data?.overallGrade,
    isLoadingGrades,
    gradesError,
  };
}
